import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useServerFn } from "@tanstack/react-start";
import { checkIsAdmin } from "@/lib/posts.functions";
import { AdminTopNav, AdminBottomNav } from "@/components/admin/AdminNav";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLayout,
});

function AdminLayout() {
  const navigate = useNavigate();
  const { user, loading, signOut } = useAuth();
  const check = useServerFn(checkIsAdmin);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  // The writing workspace uses its own minimal top bar instead of the admin chrome.
  const bare = useRouterState({ select: (r) => r.location.pathname.startsWith("/admin/yazi/") });

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    check()
      .then((r) => setIsAdmin(r.isAdmin))
      .catch(() => setIsAdmin(false));
  }, [user, loading, navigate, check]);

  if (loading || isAdmin === null) {
    return <div className="p-10 text-sm text-ink/60">Yükleniyor...</div>;
  }

  if (!isAdmin) {
    return (
      <div className="max-w-md mx-auto p-10 text-center">
        <h1 className="font-serif text-2xl mb-3">Yetkiniz yok</h1>
        <p className="text-sm text-ink/60 mb-4">
          Bu hesap admin değil. Lovable Cloud → Database → SQL Editor'da şu komutu çalıştırın:
        </p>
        <pre className="text-left text-xs bg-ink/5 p-3 rounded overflow-x-auto">
{`INSERT INTO public.user_roles (user_id, role)
VALUES ('${user?.id}', 'admin');`}
        </pre>
        <button
          onClick={() => signOut().then(() => navigate({ to: "/login" }))}
          className="mt-6 text-xs underline"
        >
          Çıkış yap
        </button>
      </div>
    );
  }

  if (bare) return <Outlet />;

  return (
    <div className="min-h-screen w-full bg-paper">
      <AdminTopNav onSignOut={() => signOut().then(() => navigate({ to: "/login" }))} />
      <main className="max-w-6xl w-full min-w-0 mx-auto px-4 sm:px-8 py-8 pb-24 md:pb-10 overflow-x-auto">
        <Outlet />
      </main>
      <AdminBottomNav />
    </div>
  );
}
